/**
 * Minhas viagens.
 *
 * A tela que a separação entre conta e viagem tornou necessária: uma conta
 * recém-criada tem ZERO viagens, e isso é estado legítimo — não erro, não tela
 * de falha. Daí o vazio ter as duas saídas reais do produto lado a lado: criar
 * uma viagem, ou entrar numa com convite.
 *
 * Arquivadas ficam separadas e recolhidas: continuam acessíveis (o histórico
 * financeiro e o roteiro seguem lá), sem competir com a viagem que está
 * acontecendo.
 */
import { calendarDate, diffCalendarDays, formatDateRange, resolveToday } from '@nv/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { listTrips, type TripSummary } from '../../auth/api';
import { SairSheet } from '../../auth/SairSheet';
import { useAuthStore } from '../../auth/store';
import { guardarViagemAtiva, lerViagemAtiva } from '../../trip/viagemAtiva';
import { BarraVoltar } from '../../ui/BarraVoltar';
import { Icon } from '../../ui/icons';
import { Card, EmptyState, ErrorState, SectionLabel, Skeleton } from '../../ui/primitives';
import { Sheet } from '../../ui/Sheet';
import { FotoAvatar, useMeuAvatar } from '../perfil/FotoAvatar';
import { useCapaDaViagem } from './capa';
import { SairDaViagem } from './SairDaViagem';

function periodo(viagem: TripSummary): string {
  // Data vinda do servidor: se algum dia vier torta, a lista mostra o cru em
  // vez de derrubar a tela inteira.
  try {
    return formatDateRange(calendarDate(viagem.startDate), calendarDate(viagem.endDate));
  } catch {
    return `${viagem.startDate} → ${viagem.endDate}`;
  }
}

/**
 * Cor do avatar de cada viagem: a paleta de famílias, pela posição na lista.
 * Não é dado da viagem — é só para as linhas não serem todas iguais.
 */
const corDaViagem = (indice: number) => `var(--family-${(indice % 6) + 1})`;

/**
 * Onde a viagem está no tempo, no fuso dela: "Faltam 35 dias", "Dia 3 de 22",
 * "Terminou". Datas tortas do servidor não derrubam o cartão: somem da pílula.
 */
export function situacaoDaViagem(viagem: Pick<TripSummary, 'startDate' | 'endDate' | 'timezone'>, agora: Date = new Date()): string | null {
  try {
    const hoje = resolveToday(viagem.timezone, agora);
    const inicio = calendarDate(viagem.startDate);
    const fim = calendarDate(viagem.endDate);
    const faltam = diffCalendarDays(inicio, hoje);
    if (faltam > 1) return `Faltam ${faltam} dias`;
    if (faltam === 1) return 'Falta 1 dia';
    if (faltam === 0) return 'Começa hoje';
    if (diffCalendarDays(fim, hoje) >= 0) return `Dia ${1 - faltam} de ${diffCalendarDays(fim, inicio) + 1}`;
    return 'Terminou';
  } catch {
    return null;
  }
}

const inicialDe = (texto: string) => (texto.trim()[0] ?? '?').toUpperCase();

const PAPEL: Record<TripSummary['role'], string> = {
  owner: 'Você é dono',
  admin: 'Administrador',
  collaborator: 'Colaborador',
  participant: 'Participante',
};

/** A imagem da viagem (item 22) quando há; senão a inicial na cor da posição. */
function CapaOuInicial({ viagem, indice }: { viagem: TripSummary; indice: number }) {
  const capa = useCapaDaViagem(viagem.id, Boolean(viagem.coverKey));
  if (capa.data?.url) {
    return <img className="nv-avatar viagens__avatar viagens__avatar--capa" src={capa.data.url} alt="" aria-hidden="true" data-testid="viagem-capa" />;
  }
  return (
    <span className="nv-avatar viagens__avatar" style={{ background: corDaViagem(indice) }} aria-hidden="true">
      {inicialDe(viagem.name)}
    </span>
  );
}

/**
 * A viagem ativa (a última aberta neste aparelho) em destaque — refino final:
 * cartão de 176 em brand-tint com a arte do voo, a situação numa pílula, o
 * nome 22/900, o período e o papel. As ações da linha (arquivar, sair)
 * continuam aqui, num alvo de 40 no canto.
 */
function CartaoAtiva({ viagem, onAbrir, acao }: { viagem: TripSummary; onAbrir: () => void; acao: ReactNode }) {
  const situacao = situacaoDaViagem(viagem);
  // A imagem da viagem (item 22) continua aparecendo: no cartão, num quadrado no alto.
  const capa = useCapaDaViagem(viagem.id, Boolean(viagem.coverKey));
  return (
    <div className="viagens__ativa" data-testid="viagem-ativa">
      <button type="button" className="viagens__linha viagens__ativa-botao" onClick={onAbrir} aria-label={`Abrir ${viagem.name}`}>
        <img className="viagens__ativa-arte" src="/ilustracoes/background-card-flight.png" alt="" aria-hidden="true" width={200} height={180} />
        <span className="viagens__ativa-topo">
        {capa.data?.url && <img className="viagens__ativa-capa" src={capa.data.url} alt="" aria-hidden="true" data-testid="viagem-capa" />}
        {situacao && (
          <span className="viagens__ativa-situacao">
            <span className="viagens__ativa-ponto" aria-hidden="true" />
            {situacao}
          </span>
        )}
        </span>
        <span className="viagens__ativa-rodape">
          <span className="viagens__ativa-texto">
            <span className="viagens__ativa-nome">{viagem.name}</span>
            <span className="viagens__ativa-periodo">
              {periodo(viagem)} · {viagem.primaryDestination}
            </span>
          </span>
          <span className="viagens__ativa-papel">{PAPEL[viagem.role]}</span>
        </span>
      </button>
      {acao && <span className="viagens__ativa-acao">{acao}</span>}
    </div>
  );
}

export function MinhasViagensScreen() {
  const navegar = useNavigate();
  const [params] = useSearchParams();
  const { state } = useLocation() as { state?: { aviso?: string } };
  const [mostrarArquivadas, setMostrarArquivadas] = useState(false);

  // A escolha de viagem é guardada POR CONTA: sobra de outra conta neste
  // aparelho não vira a viagem de quem está entrando agora.
  const userId = useAuthStore((s) => s.user?.id ?? '');
  const user = useAuthStore((s) => s.user);
  const signOut = useAuthStore((s) => s.signOut);
  const meuAvatar = useMeuAvatar();
  // Sem viagem não há Home nem Mais: esta tela precisa da própria saída
  // (teste físico, 13/09 — "entrei com a conta errada e não consigo sair").
  // Fica discreta, no rodapé, e não no cabeçalho (PO, 18/09: o avatar ali
  // não fazia sentido).
  const [conta, setConta] = useState(false);

  const viagens = useQuery({ queryKey: ['viagens'], queryFn: listTrips });
  const qc = useQueryClient();
  // Arquivar (a "exclusão" segura do beta, PO 18/09): só o dono, sempre com
  // confirmação; a viagem some da lista ativa e vai para "Arquivadas".
  const [arquivando, setArquivando] = useState<TripSummary | null>(null);
  const [arquivandoErro, setArquivandoErro] = useState<string | null>(null);
  const [arquivandoOcupado, setArquivandoOcupado] = useState(false);
  // Excluir de vez (PO, 22/09/2026): só o dono, só arquivada, e com o nome
  // digitado igual — a única ação irreversível do app.
  const [excluindo, setExcluindo] = useState<TripSummary | null>(null);
  const [nomeDigitado, setNomeDigitado] = useState('');
  const [excluirErro, setExcluirErro] = useState<string | null>(null);
  const [excluirOcupado, setExcluirOcupado] = useState(false);
  async function excluir(v: TripSummary) {
    setExcluirOcupado(true);
    setExcluirErro(null);
    try {
      const res = await fetch(`/api/v1/trips/${v.id}`, {
        method: 'DELETE',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ name: nomeDigitado }),
      });
      if (!res.ok) {
        const corpo = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(corpo.message ?? `Falha (${res.status})`);
      }
      await qc.invalidateQueries({ queryKey: ['viagens'] });
      setExcluindo(null);
      setNomeDigitado('');
    } catch (e) {
      setExcluirErro(e instanceof Error ? e.message : 'Não foi possível excluir.');
    } finally {
      setExcluirOcupado(false);
    }
  }

  async function arquivar(v: TripSummary, archived: boolean) {
    setArquivandoOcupado(true);
    setArquivandoErro(null);
    try {
      const res = await fetch(`/api/v1/trips/${v.id}/archive`, { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ archived }) });
      if (!res.ok) {
        const corpo = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(corpo.message ?? `Falha (${res.status})`);
      }
      await qc.invalidateQueries({ queryKey: ['viagens'] });
      setArquivando(null);
    } catch (e) {
      setArquivandoErro(e instanceof Error ? e.message : 'Não foi possível arquivar.');
    } finally {
      setArquivandoOcupado(false);
    }
  }

  function abrir(viagem: TripSummary) {
    // A viagem inteira, não só o id: é ela que a Home desenha, inclusive antes
    // de a lista chegar do servidor e mesmo sem rede.
    guardarViagemAtiva(userId, viagem);
    navegar('/', { replace: true });
  }

  // A viagem ativa é a última aberta nesta conta/aparelho — e só conta se
  // ainda estiver na lista de ativas (não arquivada, não saiu dela).
  const ultimaGuardada = lerViagemAtiva(userId);
  const viagemAtiva = (viagens.data?.trips ?? []).find((v) => v.id === ultimaGuardada?.id && v.status !== 'archived') ?? null;
  function abrirConfiguracoes(viagem: TripSummary) {
    guardarViagemAtiva(userId, viagem);
    navegar('/viagem/configuracoes');
  }

  // Seta de voltar (PO, 19/09): esta tela fica fora da casca com barra; quando
  // há uma viagem ativa (ou histórico), ela é interna como as demais.
  const { key: chaveDeNavegacao } = useLocation();
  const temVolta = chaveDeNavegacao !== 'default' || lerViagemAtiva(userId) !== null;
  const cabecalho = (
    <>
      {temVolta && <BarraVoltar />}
      <header className="viagens__header">
        <h1 className="viagens__titulo">Minhas viagens</h1>
        <div className="viagens__acoes">
          {/* Configurações da viagem ativa (a última aberta neste aparelho):
              atalho direto, sem passar pela Home (PO, 18/09). Sem viagem
              ativa, não aparece. */}
          {viagemAtiva && (
            <button type="button" className="viagens__engrenagem" onClick={() => abrirConfiguracoes(viagemAtiva)} aria-label={`Configurações de ${viagemAtiva.name}`} data-testid="viagens-config">
              <Icon name="configuracoes" size={22} />
            </button>
          )}
          {/* Criar viagem: o "+" no cabeçalho, como nas demais telas (PO, 18/09). */}
          <Link className="mem__mais" to="/viagens/nova" aria-label="Criar viagem" data-testid="viagens-criar">
            <Icon name="criar" size={22} />
          </Link>
        </div>
      </header>
      {conta && (
        <Sheet titleId="viagens-conta-titulo" onFechar={() => setConta(false)} testId="viagens-conta-sheet">
          <div className="conta__cab">
            <FotoAvatar nome={user?.name ?? '?'} url={meuAvatar.data?.url} tamanho={48} className="conta__avatar" />
            <span className="conta__quem">
              <span className="mais-nome" id="viagens-conta-titulo">{user?.name ?? '—'}</span>
              <span className="mais-email">{user?.email ?? ''}</span>
            </span>
          </div>
          <p className="auth-note">Entrou com a conta errada? Saia e entre de novo com a certa.</p>
          <button type="button" className="nv-btn nv-btn--ghost-danger nv-btn--block" onClick={() => void signOut()} data-testid="viagens-sair">
            <Icon name="sair" size={18} /> Sair da conta
          </button>
        </Sheet>
      )}
      <SairSheet />
    </>
  );

  if (viagens.isLoading) {
    return (
      <section className={temVolta ? 'viagens viagens--com-voltar' : 'viagens'}>
        {cabecalho}
        <Skeleton height={176} raio="hero" />
        <Skeleton height={64} count={2} />
      </section>
    );
  }

  if (viagens.isError) {
    // Sem rede, mas com uma viagem já escolhida antes: dá para continuar nela.
    const ultima = lerViagemAtiva(userId);
    return (
      <section className={temVolta ? 'viagens viagens--com-voltar' : 'viagens'}>
        {cabecalho}
        <ErrorState
          title="Não foi possível carregar suas viagens"
          description="Verifique a conexão. O que já está no aparelho continua salvo."
          onRetry={() => void viagens.refetch()}
        />
        {ultima && (
          <button
            type="button"
            className="nv-btn nv-btn--secondary nv-btn--block"
            onClick={() => navegar('/', { replace: true })}
          >
            Continuar em {ultima.name || 'sua viagem'}
          </button>
        )}
      </section>
    );
  }

  const todas = viagens.data?.trips ?? [];
  const ativas = todas.filter((v) => v.status !== 'archived');
  const arquivadas = todas.filter((v) => v.status === 'archived');
  const recemEntrou = params.get('entrou');
  // A viagem ativa sobe para o cartão; a lista fica com as outras.
  const destaque = ativas.find((v) => v.id === viagemAtiva?.id) ?? null;
  const outras = destaque ? ativas.filter((v) => v.id !== destaque.id) : ativas;
  const vazio = todas.length === 0;

  const linha = (v: TripSummary, indice: number, arquivada = false) => (
    <div className="viagens__item" key={v.id}>
      <button
        type="button"
        className={arquivada ? 'viagens__linha viagens__linha--arquivada' : 'viagens__linha'}
        onClick={() => abrir(v)}
      >
        <CapaOuInicial viagem={v} indice={indice} />
        <span className="viagens__texto">
          <span className="viagens__nome">{v.name}</span>
          <span className="viagens__detalhe">
            {periodo(v)} · {v.primaryDestination}
          </span>
        </span>
        <span className="viagens__papel">{arquivada ? 'Arquivada' : PAPEL[v.role]}</span>
      </button>
      {v.role !== 'owner' && !arquivada && <SairDaViagem tripId={v.id} nome={v.name} formato="icone" />}
      {v.role === 'owner' && (
        <button
          type="button"
          className="viagens__arquivar"
          aria-label={arquivada ? `Desarquivar ${v.name}` : `Arquivar ${v.name}`}
          onClick={() => setArquivando(v)}
          data-testid={arquivada ? 'viagem-desarquivar' : 'viagem-arquivar'}
        >
          <Icon name={arquivada ? 'sincronizar' : 'excluir'} size={18} />
        </button>
      )}
      {/* Excluir de vez só aparece onde faz sentido: viagem já arquivada, do dono. */}
      {v.role === 'owner' && arquivada && (
        <button
          type="button"
          className="viagens__arquivar viagens__excluir"
          aria-label={`Excluir ${v.name} de vez`}
          onClick={() => {
            setExcluindo(v);
            setNomeDigitado('');
            setExcluirErro(null);
          }}
          data-testid="viagem-excluir"
        >
          <Icon name="excluir" size={18} />
        </button>
      )}
    </div>
  );

  return (
    <section className={temVolta ? 'viagens viagens--com-voltar' : 'viagens'}>
      {cabecalho}

      <div className={vazio ? 'viagens__centro' : 'viagens__corpo'}>
        {state?.aviso && (
          <Card variant="chip">
            <p className="auth-note" role="status">
              {state.aviso}
            </p>
          </Card>
        )}

        {recemEntrou && (
          <div className="viagens__entrou" role="status">
            <Icon name="concluir" size={18} />
            Pronto: você entrou na viagem. Toque nela para abrir.
          </div>
        )}

        {vazio ? (
          <EmptyState
            ilustracao="vazioViagens"
            title="Você ainda não tem viagens"
            description="Crie a sua ou entre numa que já existe com o convite que você recebeu."
          />
        ) : (
          <>
            {destaque && (
              <CartaoAtiva
                viagem={destaque}
                onAbrir={() => abrir(destaque)}
                acao={
                  destaque.role === 'owner' ? (
                    <button
                      type="button"
                      className="viagens__arquivar"
                      aria-label={`Arquivar ${destaque.name}`}
                      onClick={() => setArquivando(destaque)}
                      data-testid="viagem-arquivar"
                    >
                      <Icon name="excluir" size={18} />
                    </button>
                  ) : (
                    <SairDaViagem tripId={destaque.id} nome={destaque.name} formato="icone" />
                  )
                }
              />
            )}
            {destaque && outras.length > 0 && <SectionLabel>Outras viagens</SectionLabel>}
            {(outras.length > 0 || ativas.length === 0) && (
            <Card variant="surf" className="viagens__lista">
              {outras.map((v) => linha(v, ativas.indexOf(v)))}
              {ativas.length === 0 && (
                <p className="auth-note" style={{ padding: 14 }}>
                  Todas as suas viagens estão arquivadas. Abra o histórico abaixo
                  ou crie uma nova.
                </p>
              )}
            </Card>
            )}
          </>
        )}

        {arquivadas.length > 0 && (
          <>
            <button
              type="button"
              className="viagens__arquivadas-toggle"
              onClick={() => setMostrarArquivadas((v) => !v)}
              aria-expanded={mostrarArquivadas}
            >
              <Icon name={mostrarArquivadas ? 'fechar' : 'documento'} size={20} />
              <span>Arquivadas ({arquivadas.length})</span>
              <Icon name="avancar" size={18} />
            </button>
            {mostrarArquivadas && (
              <Card variant="surf" className="viagens__lista">
                {arquivadas.map((v, i) => linha(v, ativas.length + i, true))}
              </Card>
            )}
          </>
        )}
      </div>

      {/* As duas saídas reais do produto, sempre no mesmo lugar. Sem viagem,
          criar é o primário e vem em cima; com viagens, entrar com convite
          fica à esquerda e criar à direita. */}
      <div className={vazio ? 'viagens__rodape viagens__rodape--vazio' : 'viagens__rodape'}>
        {vazio ? (
          <>
            <Link className="nv-btn nv-btn--primary" to="/viagens/nova">
              Criar uma viagem
            </Link>
            <Link className="nv-btn nv-btn--secondary" to="/convite">
              Entrar com convite
            </Link>
          </>
        ) : (
          <Link className="nv-btn nv-btn--secondary nv-btn--block" to="/convite">
            Entrar com convite
          </Link>
        )}
        <button type="button" className="viagens__sair-link" onClick={() => setConta(true)} data-testid="viagens-conta">
          {user?.name ? `${user.name} · Sair da conta` : 'Sair da conta'}
        </button>
      </div>

      {arquivando && (
        <Sheet titleId="viagem-arquivar-titulo" onFechar={() => setArquivando(null)} testId="viagem-arquivar-sheet" className="sheet__panel--confirmar">
          {/* Padrão de confirmação do refino final: selo, título, frase, dois botões. */}
          <span className={arquivando.status === 'archived' ? 'sheet__selo' : 'sheet__selo sheet__selo--perigo'} aria-hidden="true">
            <Icon name={arquivando.status === 'archived' ? 'sincronizar' : 'documento'} size={26} />
          </span>
          <h2 className="sheet__title" id="viagem-arquivar-titulo">
            {arquivando.status === 'archived' ? 'Desarquivar esta viagem?' : 'Arquivar esta viagem?'}
          </h2>
          <p className="sheet__text">
            <b>{arquivando.name}</b>
            {arquivando.status === 'archived'
              ? ' volta para a lista de viagens de todo mundo do grupo.'
              : ' sai da lista de viagens de todo mundo do grupo e vai para "Arquivadas". Nada é apagado: roteiro, gastos e fotos ficam guardados, e você pode desarquivar depois.'}
          </p>
          {arquivandoErro && (
            <p className="auth-note sug__erro" role="alert">
              {arquivandoErro}
            </p>
          )}
          <div className="sheet__acoes">
            <button type="button" className="nv-btn nv-btn--secondary" onClick={() => setArquivando(null)} disabled={arquivandoOcupado}>
              Manter
            </button>
            <button
              type="button"
              className={arquivando.status === 'archived' ? 'nv-btn nv-btn--primary' : 'nv-btn nv-btn--danger'}
              onClick={() => void arquivar(arquivando, arquivando.status !== 'archived')}
              disabled={arquivandoOcupado}
              data-testid="viagem-arquivar-confirma"
            >
              {arquivandoOcupado ? 'Aguarde…' : arquivando.status === 'archived' ? 'Desarquivar' : 'Arquivar'}
            </button>
          </div>
        </Sheet>
      )}

      {excluindo && (
        <Sheet titleId="viagem-excluir-titulo" onFechar={() => setExcluindo(null)} testId="viagem-excluir-sheet" className="sheet__panel--confirmar">
          <span className="sheet__selo sheet__selo--perigo" aria-hidden="true"><Icon name="excluir" size={26} /></span>
          <h2 className="sheet__title" id="viagem-excluir-titulo">Excluir esta viagem de vez?</h2>
          <p className="sheet__text">
            <b>{excluindo.name}</b> some para todo mundo do grupo, com roteiro, gastos, fotos, enquetes e memórias.
            {' '}<b>Isto não tem volta</b> — nem para você, nem para quem participou.
          </p>
          <label className="nv-field">
            <span className="nv-field__label">Digite o nome da viagem para confirmar</span>
            <input
              className="nv-field__input"
              value={nomeDigitado}
              onChange={(e) => setNomeDigitado(e.target.value)}
              placeholder={excluindo.name}
              autoComplete="off"
              data-testid="viagem-excluir-nome"
            />
          </label>
          {excluirErro && (
            <p className="auth-error" role="alert">
              {excluirErro}
            </p>
          )}
          <div className="sheet__acoes">
            <button type="button" className="nv-btn nv-btn--secondary" onClick={() => setExcluindo(null)} disabled={excluirOcupado}>
              Manter
            </button>
            <button
              type="button"
              className="nv-btn nv-btn--danger"
              onClick={() => void excluir(excluindo)}
              disabled={excluirOcupado || nomeDigitado.trim() !== excluindo.name.trim()}
              data-testid="viagem-excluir-confirma"
            >
              {excluirOcupado ? 'Excluindo…' : 'Excluir de vez'}
            </button>
          </div>
        </Sheet>
      )}
    </section>
  );
}
